'use client';

import { useState, useRef, useEffect } from 'react';
import { useNotifications, AppNotification } from '@/contexts/NotificationContext';
import Icon from '@/components/ui/AppIcon';
import { useRouter } from 'next/navigation';

const TYPE_ICON: Record<string, { icon: any; color: string }> = {
  success: { icon: 'CheckCircleIcon', color: 'text-success' },
  error: { icon: 'XCircleIcon', color: 'text-error' },
  warning: { icon: 'ExclamationTriangleIcon', color: 'text-warning' },
  info: { icon: 'InformationCircleIcon', color: 'text-primary' },
};

function timeAgo(date: Date): string {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleNotifClick(notif: AppNotification) {
    markRead(notif.id);
    if (notif.actionUrl) {
      setOpen(false);
      router.push(notif.actionUrl);
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(p => !p)}
        className="relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-standard min-w-touch min-h-touch flex items-center justify-center"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        suppressHydrationWarning
      >
        <Icon name="BellIcon" size={20} variant="outline" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-error text-error-foreground rounded-full font-caption text-[10px] flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-popover border border-border rounded-md shadow-xl z-[300] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-heading text-sm font-semibold text-foreground">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="font-caption text-xs text-primary hover:text-secondary transition-standard"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <ul className="max-h-80 overflow-y-auto divide-y divide-border">
            {notifications.length === 0 ? (
              <li className="px-4 py-8 text-center">
                <Icon name="BellSlashIcon" size={28} className="text-muted-foreground mx-auto mb-2" variant="outline" />
                <p className="font-caption text-xs text-muted-foreground">No notifications yet</p>
              </li>
            ) : (
              notifications.map(notif => {
                const { icon, color } = TYPE_ICON[notif.type] || TYPE_ICON.info;
                return (
                  <li
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`px-4 py-3 flex gap-3 transition-standard hover:bg-muted cursor-pointer ${!notif.read ? 'bg-primary/5' : ''}`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 ${color}`}>
                      <Icon name={icon} size={16} variant="solid" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-medium text-foreground truncate">{notif.title}</p>
                      <p className="font-caption text-xs text-muted-foreground line-clamp-2">{notif.message}</p>
                      {notif.actionUrl && notif.actionLabel && (
                        <span className="font-caption text-xs text-primary mt-0.5 inline-block">
                          {notif.actionLabel} →
                        </span>
                      )}
                      <p className="font-caption text-xs text-muted-foreground mt-1">{timeAgo(notif.time)}</p>
                    </div>
                    {!notif.read && (
                      <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" aria-hidden="true" />
                    )}
                  </li>
                );
              })
            )}
          </ul>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2.5 border-t border-border">
              <p className="font-caption text-xs text-muted-foreground text-center">
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
